import React, { useState, useEffect } from 'react';
import { Plus, Edit, AlertCircle, Building, Save, X } from 'lucide-react';
import {
  getEnterprises,
  createEnterprise,
  updateEnterprise,
  deactivateEnterprise,
} from '../services/api';
import { authService } from '../services/authService';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';
import { AdminAddButton } from './admin/AdminAddButton';
import { useAdminToast } from './admin/AdminToast';
import { useConfirmDialog } from './admin/ConfirmDialog';
import { AdminActionButton, AdminRowActions } from './admin/AdminActionButton';

const EnterpriseManagement = () => {
  const [enterprises, setEnterprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { showSuccess, toastElement } = useAdminToast();
  const { confirm, confirmDialog } = useConfirmDialog();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEnterprise, setEditingEnterprise] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    loadEnterprises();
  }, []);

  const loadEnterprises = async () => {
    setError('');
    try {
      const data = await getEnterprises();
      setEnterprises(data);
    } catch (error) {
      console.error('Error loading enterprises:', error);
      setError('Failed to load enterprises');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEnterprise = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await createEnterprise(formData);
      await loadEnterprises();
      setShowCreateForm(false);
      resetForm();
      showSuccess(`Enterprise "${formData.name}" created`);
    } catch (error) {
      console.error('Error creating enterprise:', error);
      setError(error.response?.data?.message || 'Failed to create enterprise');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateEnterprise = async (e) => {
    e.preventDefault();
    if (!editingEnterprise) return;
    setSubmitting(true);

    setError('');
    try {
      await updateEnterprise(editingEnterprise.id, formData);
      await loadEnterprises();
      setEditingEnterprise(null);
      setShowCreateForm(false);
      resetForm();
      showSuccess(`Enterprise "${formData.name}" updated`);
    } catch (error) {
      console.error('Error updating enterprise:', error);
      setError(error.response?.data?.message || 'Failed to update enterprise');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisableEnterprise = async (id) => {
    const enterprise = enterprises.find((ent) => ent.id === id);
    const label = enterprise ? enterprise.name : 'this enterprise';
    const confirmed = await confirm({
      title: `Deactivate enterprise "${label}"?`,
      message: `"${label}" will no longer be selectable elsewhere in the app.`,
      confirmLabel: 'Deactivate',
    });
    if (!confirmed) return;

    setError('');
    try {
      await deactivateEnterprise(id);
      await loadEnterprises();
      showSuccess(`Enterprise "${label}" disabled`);
    } catch (error) {
      console.error('Error disabling enterprise:', error);
      setError(error.response?.data?.message || 'Failed to disable enterprise');
    }
  };

  const startEdit = (enterprise) => {
    setEditingEnterprise(enterprise);
    setError('');
    setFormData({
      name: enterprise.name || '',
      description: enterprise.description || '',
    });
    setShowCreateForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
    });
  };

  if (loading) return <AdminLoading message="Loading enterprises..." />;

  return (
    <div className="space-y-6">
      {toastElement}
      {confirmDialog}
      <AdminPageHeader
        icon={Building}
        title="Enterprise Management"
        subtitle="Manage internal enterprises"
        rightContent={
          !showCreateForm && (
            <AdminAddButton
              onClick={() => {
                setShowCreateForm(true);
                setEditingEnterprise(null);
                setError('');
                resetForm();
              }}
              label="Add Enterprise"
            />
          )
        }
      />

      {error && !showCreateForm && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 shadow-sm">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900 flex items-center">
              {editingEnterprise ? (
                <>
                  <Edit className="h-6 w-6 mr-2 text-blue-600" />
                  Edit Enterprise
                </>
              ) : (
                <>
                  <Plus className="h-6 w-6 mr-2 text-blue-600" />
                  Create New Enterprise
                </>
              )}
            </h3>
            <button
              onClick={() => {
                setShowCreateForm(false);
                setEditingEnterprise(null);
                setError('');
                resetForm();
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-4 shadow-sm mb-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={editingEnterprise ? handleUpdateEnterprise : handleCreateEnterprise} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Enterprise Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Enter enterprise name"
                required
                disabled={submitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Enter description"
                rows="3"
                disabled={submitting}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingEnterprise(null);
                  setError('');
                  resetForm();
                }}
                className="px-5 py-2.5 border border-gray-300 rounded-xl shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={submitting}
              >
                <X className="h-4 w-4 inline mr-2" />
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {editingEnterprise ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 inline mr-2" />
                    {editingEnterprise ? 'Update Enterprise' : 'Create Enterprise'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Enterprises Table */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Building className="h-5 w-5 mr-2 text-gray-600" />
            Enterprises ({enterprises.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {enterprises.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                    <Building className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No enterprises found. Create one to get started.</p>
                  </td>
                </tr>
              ) : (
                enterprises.map((enterprise) => (
                  <tr key={enterprise.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{enterprise.name || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{enterprise.description || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                        enterprise.active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        <span className={`w-2 h-2 rounded-full mr-2 ${
                          enterprise.active ? 'bg-green-500' : 'bg-red-500'
                        }`}></span>
                        {enterprise.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <AdminRowActions align="start">
                        <AdminActionButton variant="edit" onClick={() => startEdit(enterprise)} />
                        <AdminActionButton variant="deactivate" onClick={() => handleDisableEnterprise(enterprise.id)} />
                      </AdminRowActions>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseManagement;
